<!--
  HolidayInfoDialog.vue
    Holiday Info Input Dialog Component
-->

<!-- -------------------------------------------------------------------------------- -->
<template>
  <v-dialog v-model="mVisible" persistent max-width="400px">
    <div class="hm-hi">
      <div class="hm-bg-primary hm-hi-title">
        休暇入力
        <div class="hm-hi-delete" :class="{'hm-hide': !mIsEdit}">
          <v-btn icon absolute flat color="white" class="hm-hi-delete-icon" @click="onClickDelete"><v-icon>delete</v-icon></v-btn>
        </div>
      </div>

      <!-- 更新日時 -->
      <v-layout row wrap justify-center class="hm-hi-updatedate" :class="{'hm-hi-updatedate-disp':checkUpdated}">
        <div>(更新日時 : {{mHolidayInfo.update_date}})</div>
      </v-layout>

      <!-- 種別名称(登録済みの場合のみ) -->
      <v-layout row wrap class="hm-hi-category" v-if="mIsEdit === true">
        <v-flex xs2>
          <v-icon large color="orange darken-2" class="hm-hi-icon">category</v-icon>
        </v-flex>
        <v-flex xs8 class="hm-hi-category-text">
          <div>{{mDispCategory}}</div>
        </v-flex>
      </v-layout>

      <v-divider />

      <!-- 日付 -->
      <v-layout row wrap class="hm-hi-date">
        <v-flex xs2>
          <v-btn flat icon class="hm-hi-date-icon">
            <v-icon large color="green darken-4">calendar_today</v-icon>
          </v-btn>
        </v-flex>

        <v-flex xs5>
          <v-menu
            ref="mDpicker"
            v-model="mDpicker"
            :close-on-content-click="false"
            :return-value.sync="mSelDate"
            lazy
            transition="scale-transition"
            offset-y
            full-width
            :nudge-right="40"
            max-width="290px"
            min-width="290px"
            v-bind:disabled="mIsEdit"
           >
            <v-text-field
              class="hm-hi-text-date mr-3"
              slot="activator"
              v-model="mSelDate"
              readonly
              background-color="grey lighten-2"
              v-bind:disabled="mIsEdit"
            ></v-text-field>
            <v-date-picker v-model="mSelDate" scrollable>
              <v-btn flat color="primary" @click="mDpicker = false">Cancel</v-btn>
              <v-btn flat color="primary" @click="$refs.mDpicker.save(mSelDate)">OK</v-btn>
            </v-date-picker>
          </v-menu>
        </v-flex>
      </v-layout>

      <v-divider />

      <!-- コメント -->
      <v-layout row wrap class="hm-hi-memo">
        <v-flex xs2><v-icon large color="blue">message</v-icon></v-flex>
        <v-flex xs8>
          <v-form v-model="mHiFormValid">
            <v-textarea
              v-model="mComment"
              :rules="[mHiMsgRules.counter]"
              label="Comment"
              rows=2
              box
              caption
            >
            </v-textarea>
          </v-form>
        </v-flex>
        <v-flex xs1>
          <v-btn small icon flat color="primary" @click="onClickMessageClear">
            <v-icon>backspace</v-icon>
          </v-btn>
        </v-flex>
       </v-layout>

      <v-divider />

      <!-- 休暇ボタン -->
      <div class="hm-hi-cntrl hm-hi-cntrl-upper">
        <v-btn
          color="primary"
          slot="activator"
          class="white--text hm-hi-btn"
          @click="onClickSendHolidayInfo(mHolidayCategory.PAID)"
        >
          有給休暇
        </v-btn>
        <v-btn
          color="primary"
          slot="activator"
          class="white--text hm-hi-btn"
          @click="onClickSendHolidayInfo(mHolidayCategory.SPECIAL)"
        >
          特別休暇
        </v-btn>
      </div>

      <div class="hm-hi-cntrl hm-hi-cntrl-bottom">
        <v-btn
          color="primary"
          slot="activator"
          class="white--text hm-hi-btn"
          @click="onClickSendHolidayInfo(mHolidayCategory.SUBSTITUTE)"
        >
          代休
        </v-btn>
        <v-btn
          color="primary"
          slot="activator"
          class="white--text hm-hi-btn"
          @click="onClickSendHolidayInfo(mHolidayCategory.ABSENCE)"
        >
          欠勤
        </v-btn>
      </div>

      <v-btn
        color="secondary"
        @click="onClickClose"
      >
        CLOSE
      </v-btn>
    </div>

    <!-- ダイアログ設定 -->
    <errorinfo-dialog ref="errorInfoDialog">
      <div slot="msg">{{ mErrMsg }}</div>
    </errorinfo-dialog>
    <systeminfo-dialog ref="systemInfoDialog">
      <div slot="msg">{{ mSysMsg }}</div>
    </systeminfo-dialog>
    <confirm-dialog ref="confirmDialog">
      <div slot="msg">{{ mConfirmMsg }}</div>
    </confirm-dialog>
  </v-dialog>
</template>

<!-- -------------------------------------------------------------------------------- -->
<script>
import { mapState } from 'vuex'
import {
  HolidayCategory,
  HolidayCategoryNames,
  HolidayCategoryDiff,
  getNowDatetimeMoment,
  formatDateMoment,
  isNullorUndefined,
} from '@/common'
import ErrorInfoDialog from '@/components/dialogs/ErrorInfoDialog'
import SystemInfoDialog from '@/components/dialogs/SystemInfoDialog'
import ConfirmDialog from '@/components/dialogs/ConfirmDialog'

export default {
  name: 'HolidayInfo',
  data: () => ({
    mVisible: false,
    mIsEdit: false,
    mDispCategory: '',
    mSelDate: '',
    mComment: '',
    mHiFormValid: true,
    mHiMsgRules: {
      counter: v => ((v == null) || (v.length <= 100)) || '100文字以内で入力してください',
    },
    mErrMsg: '',
    mSysMsg: '',
    mConfirmMsg: '',
    mDpicker: false,
    mHolidayInfo: {},
    mHolidayCategory: HolidayCategory,
    mHolidayCategoryNames: HolidayCategoryNames,
    mHolidayCategoryDiff: HolidayCategoryDiff,
  }),
  components: {
    'errorinfo-dialog': ErrorInfoDialog,
    'systeminfo-dialog': SystemInfoDialog,
    'confirm-dialog': ConfirmDialog,
  },
  mounted: function () {
    this.$log.debug('# HolidayInfo')
    // ダイアログオープンイベントハンドラを登録
    this.$on('onClickHolidayInfo', this.onClickHolidayInfo)
  },
  destroyed: function () {
    this.$log.debug('# HolidayInfo')
  },
  computed: {
    ...mapState({
      mTeleworkList: state => state.telework.sTeleworkList,
    }),
    checkUpdated: function () {
      return (!isNullorUndefined(this.mHolidayInfo.update_date) && this.mHolidayInfo.update_date !== '')
    },
  },
  methods: {
    // -------------------------------------------------------------------------
    // A Handler on Holiday Info Dialog Open
    onClickHolidayInfo: async function (data) {
      //this.$log.debug('★[onClickHolidayInfo] クリックしたデータ(' + JSON.stringify(data) + ')')

      if (data.list_row !== -1 &&
          !this.checkTeleworkDataContext(data.list_row)) {
        this.mSysMsg = '勤怠データが登録済みのため、休暇情報を登録できません。'
        this.$refs.systemInfoDialog.$emit('show')
        return
      }

      this.mHolidayInfo = data
      this.mHiFormValid = true

      // 種別
      if (!isNullorUndefined(data.category) && data.category !== 0) {
        this.mDispCategory = this.mHolidayCategoryNames[Number(data.category) - this.mHolidayCategoryDiff - 1]
      }
      else {
        this.mDispCategory = '(未登録)'
      }

      // 日付
      if (!isNullorUndefined(data.date)) {
        this.mIsEdit = true
        this.mSelDate = data.date
      }
      else {
        this.mIsEdit = false
        this.mSelDate = formatDateMoment(getNowDatetimeMoment(), 'YYYY-MM-DD')
      }

      // コメント
      if (!isNullorUndefined(data.comment) || data.comment !== '') {
        this.mComment = data.comment
      }
      else {
        this.mComment = ''
      }

      this.mVisible = true  // Show Dialog
    },
    // -------------------------------------------------------------------------
    // On Click Send Holiday Info
    onClickSendHolidayInfo: async function (category) {
      //this.$log.debug('★[onClickSendHolidayInfo]種別(' + category + ')')
      if (!this.mHiFormValid) {
        return
      }

      let date = this.mSelDate.replace(/-/g, '')
      let send_data = {
        list_row: -1,
        company_id: this.$store.getters.loginUserCompanyId,
        staff_id: this.$store.getters.loginUserBaseInfo.staff_id,
        workinghour_id: null,
        date: date,
        category: category,
        comment: this.mComment,
        linked_date: date,
        updated: 0
      }

      this.mSysMsg = ''
      if (isNullorUndefined(this.mHolidayInfo.workinghour_id)) {
        // workinghour_id未設定 → 登録

        if (!isNullorUndefined(this.mHolidayInfo.list_row) && this.mHolidayInfo.list_row !== -1) {
          // 勤務データ一覧画面での登録の場合
          send_data.list_row = this.mHolidayInfo.list_row
          send_data.updated = 1   // 変更有無：1(変更有)
          this.mSysMsg = '登録しました。'
        }
      }
      else {
        // workinghour_idあり → 編集
        send_data.list_row = this.mHolidayInfo.list_row
        send_data.workinghour_id = this.mHolidayInfo.workinghour_id
        send_data.updated = 1   // 変更有無：1(変更有)
        this.mSysMsg = '更新しました。'
      }
      this.$log.debug('★[onClickSendHolidayInfo]送信データ(' + JSON.stringify(send_data) + ')')

      const result = await this.$store.dispatch('aSendHolidayInfo', send_data)
      //this.$log.debug('★  処理結果(' + JSON.stringify(result) + ')')
      if (result.body.mismatch) {
        // 送信すれ違いにより、すでに別の勤怠データが登録されている場合
        this.mSysMsg = '勤怠または休暇データが登録済みのため、休暇情報を登録できません。'
        this.$refs.systemInfoDialog.$emit('show')
        return
      }
      if (!result.error && !result.body.error) {
        if (this.mSysMsg !== '') {
          this.$refs.systemInfoDialog.$emit('show')
        }
        // 行先情報送信
        this.$emit('onUpdateWhereabouts')
        this.onClickClose()
      }
      else {
        this.mErrMsg = 'サーバ処理でエラーが発生しました。しばらくしてから再度実行して下さい。'
        this.$refs.errorInfoDialog.$emit('show')
      }
    },
    // -------------------------------------------------------------------------
    // On Click Delete Button
    onClickDelete: function () {
      this.mConfirmMsg = '削除してもよろしいですか？'
      this.$refs.confirmDialog.$emit('show', this.deleteOkCallback)
    },
    // -------------------------------------------------------------------------
    // callback on accept delete
    deleteOkCallback: async function () {
      this.$log.debug('★[deleteOkCallback]start  mHolidayInfo(' + JSON.stringify(this.mHolidayInfo) + ')')
      const send_data = {
        list_row: this.mHolidayInfo.list_row,
        workinghour_id: this.mHolidayInfo.workinghour_id
      }
      this.$log.debug('send : ' + JSON.stringify(send_data))

      let result = await this.$store.dispatch('aDeleteHolidayInfo', send_data)
      this.$log.debug('result : ' + JSON.stringify(result))
      if ((!result.error) && (!result.body.error)) {
        this.mSysMsg = '削除しました。'
        this.$refs.systemInfoDialog.$emit('show')
        this.onClickClose()
      }
      else {
        this.mErrMsg = '削除に失敗しました。しばらくしてから再度実行して下さい。'
        this.$refs.errorInfoDialog.$emit('show')
      }
    },
    // -------------------------------------------------------------------------
    // On Click Close
    onClickClose: function () {
      this.mHiFormValid = true
      this.mVisible = false
    },
    // -------------------------------------------------------------------------
    // On Click Message Clear
    onClickMessageClear: function () {
      this.mComment = ''
    },
    // -------------------------------------------------------------------------
    // check the context of send data
    // [input] date : YYYY-MM-dd形式
    // [out]  true : 同日登録済みの勤怠データなし
    //        false: 同日登録済みの勤怠データあり
    checkTeleworkDataContext: function (listRow) {
      //this.$log.debug('★[checkTeleworkDataContext] start (listRow:' + listRow + ')')

      // 指定の日付に、すでに勤怠・休暇データが登録されているかチェック
      if (!isNullorUndefined(this.mTeleworkList[listRow - 1].startId) ||
          !isNullorUndefined(this.mTeleworkList[listRow - 1].leaveId) ||
          !isNullorUndefined(this.mTeleworkList[listRow - 1].seatedId) ||
          !isNullorUndefined(this.mTeleworkList[listRow - 1].endId)) {
        // 同日登録済みの勤怠データあり
        //this.$log.debug('★  →同日データあり')
        return false
      }

      // 同日登録済みの勤怠データなし
      //this.$log.debug('★  →同日データなし')
      return true
    }
  },
}
</script>

<!-- -------------------------------------------------------------------------------- -->
<style scoped>
.hm-hi {
  text-align: center;
  background-color: whitesmoke;
}
.hm-hi-title {
  padding: 20px;
  color: white;
  font-size: 1.2em;
  font-weight: bold;
}
.hm-hi-delete {
  float: right;
  margin-top: -5px;
  margin-right: 30px;
}
.hm-hi-delete-icon {
  z-index: 210;
}
.hm-hi-icon {
  margin-top: 7px;
}
.hm-hi-cntrl {
  width: 90%;
  margin: 0 auto;
}
.hm-hi-cntrl-upper {
  margin-top: 10px;
}
.hm-hi-cntrl-bottom {
  margin-bottom: 5px;
}
.hm-hi-btn {
  width: 40%;
}
.hm-hi-updatedate {
  display: none;
  margin-top: 10px;
  font-size: 0.9em;
}
.hm-hi-updatedate-disp {
  display: block;
}
.hm-hi-category {
  height: 50px;
  line-height: 50px;
  font-size: 1.1em;
  font-weight: bold;
}
.hm-hi-category-text {
  text-align: left;
  padding-left: 5px;
}
.hm-hi-date {
  height: 60px;
  line-height: 60px;
  font-size: 1.5em;
  font-weight: bold;
}
.hm-hi-date-icon {
  padding-top: 7px;
}
.hm-hi-memo {
  margin-top: 10px;
  font-size: 0.5em;
}
</style>



// WEBPACK FOOTER //
// src/components/dialogs/HolidayInfoDialog.vue